import { useEffect, useState } from 'react';
import { Badge, Button, Modal } from './kit';
import { paymentsApi, type PaymentDetail } from '../api/payments';
import { money } from './format';
import { BankMark } from './BankMark';

const STATUS: Record<string, { tone: 'good' | 'warn' | 'bad'; label: string }> = {
  approved: { tone: 'good', label: 'Onaylandı' },
  pending: { tone: 'warn', label: 'Bekliyor' },
  failed: { tone: 'bad', label: 'Başarısız' },
};
function fmtDate(d: string) {
  return new Date(d).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
}

function Item({ k, value, mono, full }: { k: string; value: string | null; mono?: boolean; full?: boolean }) {
  return (
    <div className={`detail-item${full ? ' full' : ''}`}>
      <div className="k">{k}</div>
      <div className={`v${mono ? ' mono' : ''}`}>{value || '—'}</div>
    </div>
  );
}

/** Ödeme detay modalı — admin ve bayi panellerinde ortak kullanılır. */
export function PaymentDetailModal({ orderId, onClose }: { orderId: string | null; onClose: () => void }) {
  const [data, setData] = useState<PaymentDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;
    setData(null);
    setError(null);
    paymentsApi.detail(orderId).then(setData).catch(() => setError('Detay yüklenemedi'));
  }, [orderId]);

  const p = data?.payment;
  const c = data?.customer;
  const approved = p?.status === 'approved';
  const s = p ? STATUS[p.status] : null;

  return (
    <Modal
      open={orderId !== null}
      onClose={onClose}
      eyebrow="Tahsilat"
      title={c?.companyName || c?.fullName || 'Ödeme Detayı'}
      sub={orderId ?? undefined}
      size="lg"
      footer={
        <>
          {approved && (
            <Button
              variant="secondary"
              onClick={() => window.open(`/api/payments/${encodeURIComponent(orderId!)}/dekont`, '_blank')}
            >
              🖨 Dekont İndir (PDF)
            </Button>
          )}
          <Button onClick={onClose}>Kapat</Button>
        </>
      }
    >
      {error && <div className="auth-error">{error}</div>}
      {!data && !error && <div className="placeholder-note">Yükleniyor…</div>}

      {p && (
        <>
          <div className="detail-section">
            <div className="detail-section-title">Müşteri Bilgileri</div>
            <div className="detail-grid">
              <Item k="Firma" value={c?.companyName ?? null} />
              <Item k="Ad Soyad" value={c?.fullName ?? null} />
              <Item k="E-Posta" value={c?.email ?? null} />
              <Item k="Telefon" value={c?.phone ?? null} />
              <Item k="Vergi No" value={c?.taxNumber ?? null} mono />
              <Item k="Adres" value={c?.address ?? null} full />
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-section-title">Ödeme Bilgileri</div>
            <div className="detail-grid">
              <Item k="Sipariş No" value={p.orderId} mono />
              <Item k="Tarih" value={fmtDate(p.createdAt)} />
              <div className="detail-item">
                <div className="k">Banka</div>
                <div className="v"><BankMark bankKey={p.bankKey ?? null} /></div>
              </div>
              <Item k="Taksit" value={p.installment === 1 ? 'Tek Çekim' : `${p.installment} Taksit`} />
              {p.authCode && <Item k="Onay Kodu" value={p.authCode} mono />}
              <div className="detail-item">
                <div className="k">Ödeme Durumu</div>
                <div className="v">{s && <Badge tone={s.tone} dot>{s.label}</Badge>}</div>
              </div>
            </div>

            {p.status === 'failed' && p.errorMessage && (
              <div className="detail-bankmsg">
                <span className="detail-bankmsg-label">Banka Mesajı</span>
                <span className="detail-bankmsg-text">{p.errorMessage}</span>
              </div>
            )}

            <div className="detail-amount">
              <div className="k">Ödeme Miktarı</div>
              <div className="v tnum">{money(p.amount)}</div>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
}
